import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { HeaderNavigation } from "@/components/HeaderNavigation";
import { FooterSEO } from "@/components/FooterSEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { PropertyCard } from "@/components/PropertyCard";
import { useListings } from "@/hooks/use-listings";
import { ADANA_DISTRICTS } from "@/data/adana";
import { Building2, Filter, Search } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface ListingsPageProps {
  forcedType?: 'satilik' | 'kiralik';
}

export default function ListingsPage({ forcedType }: ListingsPageProps) {
  const { type: pathType, category: pathCategory } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { listings, loading, error } = useListings();

  const currentType = forcedType || (pathType === 'kiralik' ? 'kiralik' : 'satilik');
  const selectedCategory = searchParams.get('category') || pathCategory || 'all';
  const selectedIlce = searchParams.get('ilce') || 'all';
  const selectedMahalle = searchParams.get('mahalle') || 'all';
  const minPrice = searchParams.get('min') || '';
  const maxPrice = searchParams.get('max') || '';
  const query = searchParams.get('q') || '';
  const updateFilter = (key: string, value: string) => { const next = new URLSearchParams(searchParams); if (value && value !== 'all') next.set(key, value); else next.delete(key); setSearchParams(next, { replace: true }); };

  const filteredListings = useMemo(() => {
    return listings.filter((item) => {
      if (item.type !== currentType) return false;
      if (selectedCategory !== 'all' && item.category !== selectedCategory) return false;
      if (selectedIlce !== 'all' && item.ilceSlug !== selectedIlce) return false;
      if (selectedMahalle !== 'all' && item.mahalleSlug !== selectedMahalle) return false;
      if (minPrice && item.price < Number(minPrice)) return false;
      if (maxPrice && item.price > Number(maxPrice)) return false;
      if (query && !`${item.title} ${item.ilce} ${item.mahalle} ${item.description}`.toLocaleLowerCase('tr-TR').includes(query.toLocaleLowerCase('tr-TR'))) return false;
      return true;
    });
  }, [listings, currentType, selectedCategory, selectedIlce, selectedMahalle, minPrice, maxPrice, query]);

  const typeTitle = currentType === 'satilik' ? 'Adana Satılık Gayrimenkul İlanları' : 'Adana Kiralık Gayrimenkul İlanları';
  const typeDescription = currentType === 'satilik'
    ? 'Adana genelinde güncel satılık daire, villa, arsa, dükkan ve ofis portföyleri. İlçe, kategori ve bütçenize göre gayrimenkulleri inceleyin.'
    : 'Adana genelinde güncel kiralık daire, villa, işyeri ve ofis ilanları. Konum ve bütçenize uygun portföyleri inceleyin.';

  return (
    <div className="min-h-screen bg-background flex flex-col justify-between">
      <SEOHead
        title={typeTitle}
        description={typeDescription}
        canonicalPath={`/${currentType}${pathCategory ? '/' + pathCategory : ''}`}
        noindex={Boolean(searchParams.toString())}
        schemaJson={{ '@context': 'https://schema.org', '@type': 'ItemList', 'name': typeTitle, 'numberOfItems': filteredListings.length, 'itemListElement': filteredListings.map((item, index) => ({ '@type': 'ListItem', 'position': index + 1, 'name': item.title, 'url': `https://www.cornergayrimenkul.com.tr/ilan/${item.slug}` })) }}
      />
      <HeaderNavigation />

      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        {/* Breadcrumbs */}
        <Breadcrumbs
          items={[
            { label: currentType === 'satilik' ? 'Satılık Gayrimenkuller' : 'Kiralık Gayrimenkuller' }
          ]}
        />

        {/* Header */}
        <div className="mb-8 border-b border-border/80 pb-6">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-foreground tracking-tight mb-3">
            {typeTitle}
          </h1>
          <p className="text-muted-foreground text-lg max-w-3xl">
            {typeDescription}
          </p>
        </div>

        {/* Main Grid: Filters + Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Filter */}
          <aside className="space-y-6 bg-card p-5 rounded-xl border border-border h-fit shadow-xs">
            <h3 className="font-bold text-lg text-foreground flex items-center gap-2 border-b border-border pb-3">
              <Filter className="h-5 w-5 text-primary" /> Filtrele
            </h3>

            {/* Category */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-foreground">Kategori</label>
              <select
                aria-label="Gayrimenkul kategorisi"
                value={selectedCategory}
                onChange={(e) => updateFilter('category', e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="all">Tüm Kategoriler</option>
                <option value="daire">Daire</option>
                <option value="villa">Müstakil Villa</option>
                <option value="arsa">Arsa</option>
                <option value="isyeri">İşyeri / Dükkan</option>
                <option value="dukkan">Dükkan</option>
                <option value="ofis">Ofis</option>
              </select>
            </div>

            {/* District */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-foreground">İlçe</label>
              <select
                aria-label="İlçe"
                value={selectedIlce}
                onChange={(e) => updateFilter('ilce', e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="all">Tüm İlçeler</option>
                {ADANA_DISTRICTS.map((district) => <option key={district.slug} value={district.slug}>{district.name}</option>)}
              </select>
            </div>

            {/* Price Range */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-foreground">Fiyat Aralığı (TL)</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  aria-label="En düşük fiyat"
                  placeholder="Min"
                  value={minPrice}
                  onChange={(e) => updateFilter('min', e.target.value)}
                  className="w-1/2 h-10 px-3 rounded-md border border-input bg-background text-sm"
                />
                <input
                  type="number"
                  min="0"
                  aria-label="En yüksek fiyat"
                  placeholder="Max"
                  value={maxPrice}
                  onChange={(e) => updateFilter('max', e.target.value)}
                  className="w-1/2 h-10 px-3 rounded-md border border-input bg-background text-sm"
                />
              </div>
            </div>

            <div className="space-y-2"><label htmlFor="catalog-search" className="text-sm font-semibold text-foreground">İlan veya mahalle ara</label><input id="catalog-search" value={query} onChange={(event) => updateFilter('q', event.target.value)} placeholder="Örn. Gürselpaşa" className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" /></div>
            <Button
              variant="outline"
              className="w-full text-xs"
              onClick={() => {
                setSearchParams({}, { replace: true });
              }}
            >
              Filtreleri Sıfırla
            </Button>
          </aside>

          {/* Cards Grid */}
          <div className="lg:col-span-3">
            <p className="text-sm text-muted-foreground mb-5">{loading ? 'Güncel ilanlar yükleniyor…' : `${filteredListings.length} yayındaki ilan`}</p>
            {error ? <div role="alert" className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-amber-900"><h2 className="font-semibold text-lg">İlanlar şu anda yüklenemedi</h2><p className="text-sm mt-2">Lütfen bağlantınızı kontrol edip sayfayı yenileyin.</p></div> : loading ? <div aria-label="İlanlar yükleniyor" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{Array.from({ length: 3 }, (_, index) => <div key={index} className="animate-pulse rounded-2xl border border-border overflow-hidden"><div className="aspect-[16/10] bg-muted" /><div className="p-5 space-y-4"><div className="h-4 bg-muted rounded w-3/4" /><div className="h-3 bg-muted rounded" /><div className="h-3 bg-muted rounded w-1/2" /></div></div>)}</div> : filteredListings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredListings.map((property) => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-muted/20 rounded-2xl border border-dashed border-border p-8">
                <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Aradığınız kriterlere uygun portföy bulunamadı</h3>
                <p className="text-muted-foreground text-sm max-w-md mx-auto mb-6">
                  Aradığınız özellikteki gayrimenkul henüz web sitemize eklenmemiş olabilir. Ekibimiz sizin için özel gayrimenkul araştırması yapsın.
                </p>
                <Button asChild size="lg">
                  <a href="https://wa.me/905335487801?text=Merhaba,%20özel%20gayrimenkul%20talebim%20var." target="_blank" rel="noreferrer">
                    Talep Bırakın (WhatsApp)
                  </a>
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>

      <FooterSEO />
    </div>
  );
}
